import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  FaqAnswerObject,
  ProductDetail,
} from "../interfaces/product-interface";
import { updateProduct } from "../redux/slices/productSlice";
import { productNameTransform } from "../constants/Products";
import { AppDispatch } from "../redux/store";

interface ProductDetailsModalProps {
  open: boolean;
  onClose: () => void;
  product: ProductDetail;
  onDeleteFAQ?: (index: number) => void;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  open,
  onClose,
  product,
  onDeleteFAQ,
}) => {
  const [editedProduct, setEditedProduct] = useState<ProductDetail>(product);
  const [isEditMode, setIsEditMode] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleSaveClick = () => {
    const transformedName =
      productNameTransform[product.name as keyof typeof productNameTransform];
    dispatch(updateProduct({ name: transformedName, data: editedProduct }));
    setIsEditMode(false);
  };

  const handleInputChange = (index: number, field: string, value: string) => {
    const updatedFAQ = [...editedProduct.faq];
    const answer = updatedFAQ[index].answer;

    if (typeof answer === "string") {
      updatedFAQ[index].answer = value;
    } else {
      updatedFAQ[index].answer = {
        ...answer,
        [field as keyof typeof answer]: value,
      };
    }

    setEditedProduct({ ...editedProduct, faq: updatedFAQ });
  };

  const handleTextFieldChange = (field: string, value: string) => {
    setEditedProduct({ ...editedProduct, [field]: value });
  };

  const handleDeleteClick = (index: number) => {
    if (onDeleteFAQ) {
      onDeleteFAQ(index);
    }
  };

  const isFaqAnswerObject = (
    answer: string | FaqAnswerObject | string[]
  ): answer is FaqAnswerObject => {
    return (
      typeof answer === "object" &&
      answer !== null &&
      "description" in answer &&
      "enumerated" in answer
    );
  };

  const isStringArray = (
    answer: string | FaqAnswerObject | string[]
  ): answer is string[] => {
    return (
      Array.isArray(answer) && answer.every((item) => typeof item === "string")
    );
  };

  const isFeatureContentObject = (content: any) =>
    content &&
    typeof content === "object" &&
    typeof content.description === "string" &&
    Array.isArray(content.enumerated) &&
    content.enumerated.every((item: any) => typeof item === "string");

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Product Details</DialogTitle>
      <DialogContent>
        <Typography variant="h4">{product.name}</Typography>
        {isEditMode ? (
          <TextField
            label="Category"
            value={editedProduct.category}
            onChange={(e) => handleTextFieldChange("category", e.target?.value)}
            fullWidth
            margin="dense"
          />
        ) : (
          <Typography variant="body1">Category: {product.category}</Typography>
        )}

        {/** RENDER APPLICATION QUALIFICATION */}
        {isEditMode ? (
          <TextField
            label="Application Qualification"
            value={
              typeof editedProduct.applicationQualification === "string"
                ? editedProduct.applicationQualification
                : editedProduct.applicationQualification?.description ?? ""
            }
            onChange={(e) =>
              handleTextFieldChange("applicationQualification", e.target?.value)
            }
            fullWidth
            margin="dense"
          />
        ) : (
          <Typography variant="body1">
            Application Qualification:
            {typeof product?.applicationQualification === "string" ? (
              product.applicationQualification
            ) : (
              <>
                {product?.applicationQualification?.description && (
                  <Typography variant="body2">
                    {product.applicationQualification?.description}
                  </Typography>
                )}
                {product?.applicationQualification?.enumerated && (
                  <ul>
                    {product.applicationQualification?.enumerated.map(
                      (item, index) => (
                        <li key={index + 1}>
                          <Typography variant="body2">{item}</Typography>
                        </li>
                      )
                    )}
                  </ul>
                )}
              </>
            )}
          </Typography>
        )}

        {/** RENDER REQUIRED DOCUMENTS */}
        {isEditMode ? (
          <TextField
            label="Required Documents"
            value={editedProduct.requiredDocuments}
            onChange={(e) =>
              handleTextFieldChange("requiredDocuments", e.target?.value)
            }
            fullWidth
            margin="dense"
          />
        ) : (
          <Typography variant="body1">
            Required Documents: {product?.requiredDocuments}
          </Typography>
        )}
        <Typography variant="body1">Logo: {product?.logo}</Typography>
        <Typography variant="body1">Image: {product?.image}</Typography>

        {/** RENDER FAQs */}
        <Typography variant="h6">FAQs</Typography>
        {editedProduct.faq?.map((faqItem, index) => (
          <Box key={index + 1} mb={2}>
            {isEditMode ? (
              <div>
                <TextField
                  label="Question"
                  value={faqItem.question}
                  onChange={(e) =>
                    handleInputChange(index, "question", e.target.value)
                  }
                  fullWidth
                  margin="dense"
                />
                {typeof faqItem.answer === "string" ? (
                  <TextField
                    label="Answer"
                    value={faqItem.answer}
                    onChange={(e) =>
                      handleInputChange(index, "answer", e.target.value)
                    }
                    fullWidth
                    margin="dense"
                  />
                ) : isStringArray(faqItem.answer) ? (
                  <ul>
                    {faqItem.answer.map((item, arrayIndex) => (
                      <li key={arrayIndex + 1}>{item}</li>
                    ))}
                  </ul>
                ) : isFaqAnswerObject(faqItem.answer) ? (
                  <>
                    <TextField
                      label="Answer Description"
                      value={faqItem.answer.description ?? ""}
                      onChange={(e) =>
                        handleInputChange(index, "description", e.target.value)
                      }
                      fullWidth
                      margin="dense"
                    />
                    {faqItem.answer.enumerated &&
                      faqItem.answer.enumerated.length > 0 && (
                        <ul>
                          {faqItem.answer.enumerated?.map((item, enumIndex) =>
                            typeof item === "string" ? (
                              <li key={enumIndex + 1}>{item}</li>
                            ) : (
                              <li key={enumIndex + 1}>
                                <strong>{item.title}</strong>
                                <ul>
                                  {item.content?.map(
                                    (contentItem, contentIndex) => (
                                      <li key={contentIndex + 1}>
                                        {contentItem}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </li>
                            )
                          )}
                        </ul>
                      )}
                  </>
                ) : null}
                <IconButton onClick={() => handleDeleteClick(index)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            ) : (
              <div>
                <Typography variant="subtitle1">
                  Q: {faqItem.question}
                </Typography>
                <Typography variant="body2">
                  A:{" "}
                  {typeof faqItem.answer === "string"
                    ? faqItem.answer
                    : isStringArray(faqItem.answer)
                    ? faqItem.answer.join(", ")
                    : isFaqAnswerObject(faqItem.answer)
                    ? faqItem.answer.description ?? ""
                    : ""}
                </Typography>
                {isFaqAnswerObject(faqItem.answer) &&
                  faqItem.answer.enumerated &&
                  faqItem.answer.enumerated?.length > 0 && (
                    <ul>
                      {faqItem.answer.enumerated?.map((item, enumIndex) =>
                        typeof item === "string" ? (
                          <li key={enumIndex + 1}>{item}</li>
                        ) : (
                          <li key={enumIndex + 1}>
                            <strong>{item.title}</strong>
                            <ul>
                              {item.content?.map(
                                (contentItem, contentIndex) => (
                                  <li key={contentIndex + 1}>{contentItem}</li>
                                )
                              )}
                            </ul>
                          </li>
                        )
                      )}
                    </ul>
                  )}
              </div>
            )}
          </Box>
        ))}

        {/** RENDER FEATURES*/}
        <Typography variant="h6">Features</Typography>
        {editedProduct?.features?.map((feature, index) => (
          <Box key={index + 1} mb={2}>
            {isEditMode ? (
              <>
                <TextField
                  label="Feature Title"
                  value={feature.title ?? ""}
                  onChange={(e) =>
                    handleInputChange(index, "title", e.target?.value)
                  }
                  fullWidth
                  margin="dense"
                />
                {typeof feature.content === "string" ? (
                  <TextField
                    label="Feature Content"
                    value={feature.content || ""}
                    onChange={(e) =>
                      handleInputChange(index, "content", e.target?.value)
                    }
                    fullWidth
                    margin="dense"
                  />
                ) : isFeatureContentObject(feature.content) ? (
                  <>
                    <TextField
                      label="Feature Description"
                      value={feature.content?.description ?? ""}
                      onChange={(e) =>
                        handleInputChange(index, "description", e.target?.value)
                      }
                      fullWidth
                      margin="dense"
                    />
                    {feature.content?.enumerated &&
                      feature.content?.enumerated.length > 0 && (
                        <ul>
                          {feature.content?.enumerated.map(
                            (item, itemIndex) => (
                              <li key={itemIndex + 1}>{item}</li>
                            )
                          )}
                        </ul>
                      )}
                  </>
                ) : null}
              </>
            ) : (
              <>
                <Typography variant="subtitle1">
                  {feature.title ?? "No Title"}
                </Typography>
                {typeof feature.content === "string" ? (
                  <Typography variant="body2">
                    {feature.content || "No Content"}
                  </Typography>
                ) : isFeatureContentObject(feature.content) ? (
                  <>
                    <Typography variant="body2">
                      {feature.content?.description ?? "No Description"}
                    </Typography>
                    {feature.content?.enumerated &&
                      feature.content?.enumerated.length > 0 && (
                        <ul>
                          {feature.content?.enumerated?.map(
                            (item, itemIndex) => (
                              <li key={itemIndex + 1}>{item}</li>
                            )
                          )}
                        </ul>
                      )}
                  </>
                ) : (
                  <Typography variant="body2">No Content</Typography>
                )}
              </>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        {isEditMode ? (
          <>
            <Button onClick={handleSaveClick} color="primary">
              Save
            </Button>
            <Button onClick={() => setIsEditMode(false)} color="secondary">
              Cancel
            </Button>
          </>
        ) : (
          <Button onClick={handleEditClick} color="primary">
            Edit
          </Button>
        )}
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetailsModal;
